export function isOpen(schedules, ignoreSchedules = false) {

  if (ignoreSchedules) {
    return true;
  }

  if (!Array.isArray(schedules) || schedules.length === 0) {
    return false;
  }

  const now = new Date();

  return schedules.some((schedule) => isScheduleOpen(schedule, now, false));
}

export function openingSoonMessage(schedules, locale = "en-US") {
  const now = new Date();
  let closestOpeningTime = null;
  let closestDayOffset = null;

  for (let i = 0; i < 7; i++) {
    const futureDay = new Date(now.getTime() + i * 24 * 60 * 60 * 1000);
    const dayOfWeek = _dayKey(futureDay, locale);
    const currentTimeString = i === 0 ? now.toTimeString().split(" ")[0] : "00:00:00";

    schedules.forEach((schedule) => {
      const windows = _windowsStartingOnDay(schedule, dayOfWeek);
      windows.forEach((period) => {
        if (
          period &&
          ((i === 0 && period.startTime > currentTimeString) || i > 0) &&
          (!closestOpeningTime || period.startTime < closestOpeningTime)
        ) {
          closestOpeningTime = period.startTime;
          closestDayOffset = i;
        }
      });
    });


    if (closestOpeningTime) {
      break;
    }
  }

  if (!closestOpeningTime) {
    return "Currently, there are no upcoming opening times available.";
  }

  const openingTimeDate = new Date(`1970-01-01T${closestOpeningTime}`);
  const closestOpeningTimeFormatted = openingTimeDate.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

  if (closestDayOffset === 0) {
    return `Opening today at ${closestOpeningTimeFormatted}`;
  } else if (closestDayOffset === 1) {
    return `Opening tomorrow at ${closestOpeningTimeFormatted}`;
  } else {
    const futureDay = new Date(now.getTime() + closestDayOffset * 24 * 60 * 60 * 1000);
    const dayOfWeek = futureDay.toLocaleString(locale, { weekday: "long" });
    return `Opening ${dayOfWeek} at ${closestOpeningTimeFormatted}`;
  }
}


export function isScheduleOpen(schedule, currentTime, ignoreSchedules = false) {
  if (ignoreSchedules) {
    return true;
  }

  const currentDay = _dayKey(currentTime, "en-US");
  const prevDay = _prevDayKey(currentDay);
  const currentTimeString = currentTime.toTimeString().split(" ")[0];

  // 1) Check windows that start today
  const todayWindows = _windowsStartingOnDay(schedule, currentDay);
  for (const w of todayWindows) {
    if (_isTimeInWindowSameDay(currentTimeString, w.startTime, w.endTime, w.isOvernight)) {
      return true;
    }
  }

  // 2) Check overnight windows that started yesterday and spill into today
  const prevWindows = _windowsStartingOnDay(schedule, prevDay).filter(w => _isOvernightWindow(w));
  for (const w of prevWindows) {
    if (currentTimeString < w.endTime) {
      return true;
    }
  }

  return false;
}

function _dayKey(date, locale) {
  return date.toLocaleString(locale, { weekday: "long" }).toUpperCase();
}

function _prevDayKey(dayKey) {
  const days = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
  ];
  const idx = days.indexOf(dayKey);
  if (idx === -1) return dayKey;
  return days[(idx + 6) % 7];
}

function _isOvernightWindow(w) {
  if (!w) return false;
  if (w.isOvernight === true) return true;
  // Defensive: some sources may omit isOvernight but still encode cross-day by end < start
  return Boolean(w.startTime && w.endTime && w.endTime < w.startTime);
}

function _isTimeInWindowSameDay(currentTime, startTime, endTime, isOvernight) {
  if (!startTime || !endTime) return false;

  // Overnight window: open from startTime -> 23:59:59 on the start day
  if (isOvernight === true || endTime < startTime) {
    return currentTime >= startTime;
  }

  // Normal same-day window
  return currentTime >= startTime && currentTime < endTime;
}

function _windowsStartingOnDay(schedule, dayKey) {
  if (!schedule) return [];

  // Prefer Conecto V3 periods if present
  if (Array.isArray(schedule.periodsV3) && schedule.periodsV3.length > 0) {
    return schedule.periodsV3
      .filter(p => p && p.dayOfWeek === dayKey)
      .map(p => ({
        startTime: p.startTime,
        endTime: p.endTime,
        isOvernight: p.isOvernight,
      }))
      .filter(p => p.startTime && p.endTime);
  }

  // Legacy: schedule.periods is a dayKey -> {startTime,endTime,isOvernight} object
  const legacy = schedule?.periods?.[dayKey];
  if (!legacy || !legacy.startTime || !legacy.endTime) return [];
  return [{
    startTime: legacy.startTime,
    endTime: legacy.endTime,
    isOvernight: legacy.isOvernight,
  }];
}
